import type {
  FeedbackWithCurrentGrade,
  RecordingAssessmentFeedbackStatus,
} from "@repo/app-types/entities/recordingAssessmentFeedback";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { useCallback, useMemo, useState, type FC } from "react";
import { useTransitionRecordingAssessmentFeedbackStatusMutation } from "../../api/entities/playbook.js";
import { GradingValidationDialogContainer } from "../../features/teammatePerformance/gradingValidationDialogContainer.js";
import { TableCard } from "../layout/pageLayout.js";
import { FeedbackReviewActions } from "./feedbackReviewActions.js";
import {
  FeedbackReviewCallCell,
  FeedbackReviewExpectedGradeCell,
  FeedbackReviewGradeCell,
} from "./feedbackReviewCells.js";
import {
  getFeedbackCallDisplayLines,
  getFeedbackReviewEmptyMessage,
} from "./feedbackReviewDisplayHelpers.js";

export interface FeedbackReviewTableProps {
  /** Feedback for the selected node, already filtered to `status`. */
  feedbackItems: readonly FeedbackWithCurrentGrade[];
  /** True while the feedback list is still loading. */
  isLoading: boolean;
  /** True when the feedback list failed to load. */
  isError: boolean;
  /** Whether the selected node is a section, for empty-state wording. */
  isSectionSelected: boolean;
  /** The status the list is filtered to, for empty-state wording. */
  status: RecordingAssessmentFeedbackStatus;
}

function buildColumns(args: {
  onStatusChange: (
    feedback: FeedbackWithCurrentGrade,
    status: RecordingAssessmentFeedbackStatus,
  ) => void;
}): readonly EntityTableColumnDefinition<FeedbackWithCurrentGrade>[] {
  return [
    {
      id: "call",
      header: "Call",
      isRowHeader: true,
      className: "w-[26%]",
      getTextValue: (feedback) => {
        const { primaryName, recordedAtLabel } =
          getFeedbackCallDisplayLines(feedback);
        return `${primaryName} ${recordedAtLabel}`;
      },
      renderCell: (feedback) => <FeedbackReviewCallCell feedback={feedback} />,
    },
    {
      id: "grade",
      header: "Alpharun grade",
      className: "w-[34%]",
      renderCell: (feedback) => <FeedbackReviewGradeCell feedback={feedback} />,
    },
    {
      id: "expectedGrade",
      header: "Feedback",
      className: "w-[40%]",
      renderCell: (feedback) => (
        <div>
          <FeedbackReviewExpectedGradeCell feedback={feedback} />
          <FeedbackReviewActions
            status={feedback.status}
            onStatusChange={(status) => args.onStatusChange(feedback, status)}
          />
        </div>
      ),
    },
  ];
}

/**
 * The feedback submitted against one playbook node, newest first as the server
 * ordered it. Activating a row opens the grading-validation slideover while
 * keeping the reviewer on this tab.
 */
export const FeedbackReviewTable: FC<FeedbackReviewTableProps> = (props) => {
  const { feedbackItems, isLoading, isError, isSectionSelected, status } =
    props;
  const [selectedFeedback, setSelectedFeedback] =
    useState<FeedbackWithCurrentGrade | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [transitionFeedbackStatus] =
    useTransitionRecordingAssessmentFeedbackStatusMutation();

  const handleFeedbackSelect = useCallback(
    (feedback: FeedbackWithCurrentGrade): void => {
      setSelectedFeedback(feedback);
      setSelectedNodeId(feedback.criteria_node_id);
    },
    [],
  );

  const handleSlideoverClose = useCallback((): void => {
    setSelectedFeedback(null);
    setSelectedNodeId(null);
  }, []);

  const handleStatusChange = useCallback(
    async (
      feedback: FeedbackWithCurrentGrade,
      nextStatus: RecordingAssessmentFeedbackStatus,
    ): Promise<void> => {
      try {
        await transitionFeedbackStatus({
          playbookId: feedback.recording_assessment_template_id,
          feedbackId: feedback.id,
          currentStatus: feedback.status,
          newStatus: nextStatus,
        }).unwrap();
      } catch (error) {
        console.error("Failed to transition feedback status", error);
        showToast({
          variant: ToastVariant.Error,
          title: "Couldn't update feedback",
          description: "Refresh the page and try again.",
        });
      }
    },
    [transitionFeedbackStatus],
  );

  const columns = useMemo(
    () =>
      buildColumns({
        onStatusChange: (feedback, nextStatus) => {
          void handleStatusChange(feedback, nextStatus);
        },
      }),
    [handleStatusChange],
  );

  return (
    <>
      <TableCard>
        <EntityTable
          aria-label="Feedback"
          columns={columns}
          entities={feedbackItems}
          isLoading={isLoading}
          isError={isError}
          emptyMessage={getFeedbackReviewEmptyMessage(
            status,
            isSectionSelected,
          )}
          isFullBleed
          onEntityAction={handleFeedbackSelect}
          // Cells hold different line counts, so let them read from a shared top
          // edge instead of the browser's middle alignment.
          getRowClassName={alignCellsTop}
        />
      </TableCard>

      <SlideoverContainer
        isOpen={selectedFeedback !== null && selectedNodeId !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            handleSlideoverClose();
          }
        }}
        isDismissable
      >
        {selectedFeedback && selectedNodeId ? (
          <GradingValidationDialogContainer
            gradingId={selectedFeedback.grading_id}
            sectionOrCriteriaId={selectedNodeId}
            onCriteriaSelect={setSelectedNodeId}
          />
        ) : null}
      </SlideoverContainer>
    </>
  );
};

function alignCellsTop(): string {
  return "relative align-top";
}
